import {ChangeDetectorRef, Component, inject, ViewChild} from '@angular/core';
import {MatDialogRef} from "@angular/material/dialog";
import {
	ApplicationDetailData,
	ApplicationDetailService
} from "../../../../../../../services/front/application-detail.service";
import {
	FunctionalCapacitiesService,
	FunctionalCapacity,
	FunctionalCapacityCreateForm,
	FunctionalCapacityForm,
	FunctionalCapacityGroupCreateForm,
	FunctionalCapacityIcon
} from "../../../../../../../services/back/functional-capacities.service";
import {finalize, forkJoin, Observable, of, Subscription, switchMap, tap} from "rxjs";
import {ApplicationInstance} from "../../../../../../../services/model/new-application.model";
import {ChipsModule} from "../../../../../../global/chips/chips/chips.module";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {DesignSystemModule} from "../../../../../../design-system/design-system.module";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {NgForOf, NgIf} from "@angular/common";
import {
	DashboardPageLoaderComponent
} from "../../../../../../global/title-content-loader/dashboard-page-loader.component";
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {SnackbarService} from "../../../../../../../services/front/snackbar.service";

@Component({
	selector: 'app-functional-capacities-popup',
	imports: [
		ChipsModule,
		TranslateModule,
		DesignSystemModule,
		NgIf,
		DashboardPageLoaderComponent,
		MatMenuTrigger,
		MatMenu,
		NgForOf,
		MatCheckbox,
		ReactiveFormsModule
	],
	animations: [
		trigger('expandCollapse', [
			state('collapsed', style({
				height: '0',
				overflow: 'hidden',
				opacity: 0,
				marginTop: '0',
			})),
			state('expanded', style({
				height: '*',
				overflow: 'hidden',
				opacity: 1,
				marginTop: '17px',
			})),
			transition('collapsed <=> expanded', [
				animate('300ms ease-in-out')
			])
		]),
		trigger('rotateArrow', [
			state('collapsed', style({
				transform: 'rotate(0deg)'
			})),
			state('expanded', style({
				transform: 'rotate(90deg)'
			})),
			transition('collapsed <=> expanded', [
				animate('300ms')
			])
		])
	],
	templateUrl: './functional-capacities-popup.component.html',
	styleUrl: './functional-capacities-popup.component.scss'
})
export class FunctionalCapacitiesPopupComponent {
	constructor(private dialog: MatDialogRef<FunctionalCapacitiesPopupComponent>) {
	}
	private applicationDetailService = inject(ApplicationDetailService);
	private functionalCapacitiesService = inject(FunctionalCapacitiesService);
	private snackbarService = inject(SnackbarService);
	private translate = inject(TranslateService);
	private cdr = inject(ChangeDetectorRef);

	tenantFunctionalCapacities: FunctionalCapacity[] = [];
	filteredFunctionalCapacities: FunctionalCapacity[] = [];

	applicationFunctionalCapacities: FunctionalCapacity[] = [];
	selectedFunctionalCapacityIds: Set<string> = new Set();

	functionalCapacitiesToDelete: Set<string> = new Set();
	parentsWithSelectedChildren: Set<string> = new Set();

	initialized: boolean = false;
	_loading: boolean;

	subscription: Subscription = new Subscription();

	application: ApplicationInstance;
	tenantId: string;

	emitUpdate: boolean = false;

	mode: 'delete' | 'create-group' | 'create-child' | 'select' = 'select';

	searchInput: FormControl = new FormControl('');

	@ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

	createFunctionalCapacityGroupFormGroup: FormGroup = new FormGroup({
		name: new FormControl<string | null>(null, [Validators.required]),
		icon: new FormControl<FunctionalCapacityIcon>(FunctionalCapacityIcon.CPU_CHIP_01, [Validators.required]),
	})

	createFunctionalCapacityFormGroup: FormGroup = new FormGroup({
		group: new FormControl<FunctionalCapacity | null>(null, [Validators.required]),
		name: new FormControl<string | null>(null, [Validators.required]),
	})

	icons: FunctionalCapacityIcon[] = Object.values(FunctionalCapacityIcon);

	showIcons: boolean = false;

	ngOnInit() {
		this.subscription.add(this.applicationDetailService.getApplicationDetailDataChanges()
			.pipe(tap(data => this.setApplicationDetailData(data)))
			.subscribe(() => this.initialize()));

		this.subscription.add(this.searchInput.valueChanges
			.pipe(tap((value: string) => this.filterData(value)))
			.subscribe());

		this.subscription.add(this.dialog.backdropClick()
			.subscribe(() => this.dialog.close(this.emitUpdate)));
	}

	private setApplicationDetailData(data: ApplicationDetailData): void {
		this.tenantId = data.tenantId;
		this.application = data.instance;
	}

	initialize(): void {
		this.switchLoading()
			.pipe(
				switchMap(() => forkJoin([
					this.functionalCapacitiesService.getTenantFunctionalCapacities(this.tenantId),
					this.functionalCapacitiesService.getTenantFunctionalCapacitiesByApplicationId(this.tenantId, this.application.applicationId)
				])),
				tap(([tenantFunctionCapacities, applicationFunctionalCapacities]) => {
					this.createFunctionalCapacityFormGroup.get('group')?.setValue(tenantFunctionCapacities[0] || null, { emitEvent: false });
					this.setData(tenantFunctionCapacities, applicationFunctionalCapacities)
				}),
				finalize(() => this.switchLoading())
			).subscribe(() => this.initialized = true);
	}

	setData(tenantFunctionalCapacities: FunctionalCapacity[], applicationFunctionalCapacities: FunctionalCapacity[]): void {
		this.applicationFunctionalCapacities = applicationFunctionalCapacities;
		this.tenantFunctionalCapacities = tenantFunctionalCapacities;
		this.filteredFunctionalCapacities = tenantFunctionalCapacities;
		this.selectedFunctionalCapacityIds.clear();
		this.initializeSelectedCapacities(applicationFunctionalCapacities);
	}

	initializeSelectedCapacities(applicationFunctionalCapacities: FunctionalCapacity[]): void {
		applicationFunctionalCapacities.forEach(capacity => {
			if (!capacity.children || capacity.children.length === 0) {
				this.selectedFunctionalCapacityIds.add(capacity.id);
			}
			else if (capacity.children && capacity.children.length > 0) {
				capacity.children.forEach(child => {
					this.selectedFunctionalCapacityIds.add(child.id);
				});
			}
		});
	}

	filterData(value: string) {
		if (!value.trim()) {
			this.filteredFunctionalCapacities = [...this.tenantFunctionalCapacities];
			return;
		}

		const lowercaseValue = value.toLowerCase();

		this.filteredFunctionalCapacities = this.recursiveFilter(
			this.tenantFunctionalCapacities,
			lowercaseValue
		);
	}

	private recursiveFilter(items: FunctionalCapacity[], searchValue: string): FunctionalCapacity[] {
		return items
			.reduce((filtered: FunctionalCapacity[], item) => {
				const currentMatches = item.name.toLowerCase().includes(searchValue);

				const matchingChildren = this.recursiveFilter(item.children, searchValue);

				if (currentMatches || matchingChildren.length > 0) {
					filtered.push({
						...item,
						children: currentMatches ? item.children : matchingChildren,
						expanded: !currentMatches && matchingChildren.length > 0 ? true : item.expanded
					});
				}

				return filtered;
			}, []);
	}

	toggleMode(mode: 'delete' | 'create-group' | 'create-child' | 'select', event?: MouseEvent): void {
		if (event)
			event.stopPropagation();
		this.mode = mode;
		this.functionalCapacitiesToDelete.clear();
		this.parentsWithSelectedChildren.clear();
		this.createFunctionalCapacityGroupFormGroup.reset({ name: null, icon: FunctionalCapacityIcon.CPU_CHIP_01 });
		this.showIcons = false;
		this.searchInput.reset('');
		this.createFunctionalCapacityFormGroup.reset({ name: null, group: this.tenantFunctionalCapacities[0] || null})
		this.cdr.detectChanges();
	}

	onDeleteSelectionChange(functionalCapacity: FunctionalCapacity, event: MatCheckboxChange): void {
		if (functionalCapacity.children && functionalCapacity.children.length > 0) {
			if (event.checked) {
				this.functionalCapacitiesToDelete.add(functionalCapacity.id);
				functionalCapacity.children.forEach(child => {
					this.functionalCapacitiesToDelete.add(child.id);
				});
			} else {
				this.functionalCapacitiesToDelete.delete(functionalCapacity.id);
				functionalCapacity.children.forEach(child => {
					this.functionalCapacitiesToDelete.delete(child.id);
				});
				this.parentsWithSelectedChildren.delete(functionalCapacity.id);
			}
		} else {
			const parent = this.findParentOfCapacity(functionalCapacity.id);

			if (event.checked) {
				this.functionalCapacitiesToDelete.add(functionalCapacity.id);

				if (parent && this.areAllChildrenSelectedForDelete(parent)) {
					this.functionalCapacitiesToDelete.add(parent.id);
					this.parentsWithSelectedChildren.add(parent.id);
				} else if (parent) {
					this.parentsWithSelectedChildren.add(parent.id);
				}
			} else {
				this.functionalCapacitiesToDelete.delete(functionalCapacity.id);

				if (parent && this.functionalCapacitiesToDelete.has(parent.id)) {
					this.functionalCapacitiesToDelete.delete(parent.id);
				}

				if (parent && this.areSomeChildrenSelectedForDelete(parent)) {
					this.parentsWithSelectedChildren.add(parent.id);
				} else if (parent) {
					this.parentsWithSelectedChildren.delete(parent.id);
				}
			}
		}
	}

	onDismiss(): void {
		this.dialog.close(this.emitUpdate)
	}

	updateCapacities(): void {
		const form: FunctionalCapacityForm = {
			functionalCapacityIds: Array.from(this.selectedFunctionalCapacityIds)
		}

		this.subscription.add(this.switchLoading()
			.pipe(
				switchMap(() => this.functionalCapacitiesService.updateApplicationFunctionalCapacityList(this.tenantId, this.application.applicationId, form)),
				tap((result) => this.displayUpdatedSucceed(result)),
				finalize(() => this.switchLoading())
			).subscribe(() => this.dialog.close(true)))
	}

	deleteFunctionalCapacities(): void {
		const form: FunctionalCapacityForm = {
			functionalCapacityIds: Array.from(this.functionalCapacitiesToDelete)
		};

		this.subscription.add(
			this.switchLoading()
				.pipe(
					switchMap(() =>
						this.functionalCapacitiesService.deleteFunctionalCapacities(this.tenantId, form)
					),
					tap((result) => {
						if (result) {
							this.tenantFunctionalCapacities = this.tenantFunctionalCapacities
								.map(parent => {
									if (this.functionalCapacitiesToDelete.has(parent.id)) {
										return null;
									}

									const remainingChildren = parent.children?.filter(
										child => !this.functionalCapacitiesToDelete.has(child.id)
									) || [];

									return {
										...parent,
										children: remainingChildren
									};
								})
								.filter(Boolean) as FunctionalCapacity[];

							this.filteredFunctionalCapacities = this.tenantFunctionalCapacities;

							this.functionalCapacitiesToDelete.clear();
							this.parentsWithSelectedChildren.clear();

							this.displayUpdatedSucceed(true);

							this.toggleMode('select')

							this.emitUpdate = true;
						} else {
							this.displayUpdatedSucceed(false);
						}
					}),
					finalize(() => this.switchLoading())
				)
				.subscribe()
		);
	}


	toggleAccordion(functionalCapacity: FunctionalCapacity): void {
		functionalCapacity.expanded = !functionalCapacity.expanded;
	}

	switchLoading(): Observable<{}> {
		this._loading = !this._loading;
		return of({})
	}

	isFunctionalCapacitySelected(functionalCapacity: FunctionalCapacity): boolean {
		return this.selectedFunctionalCapacityIds.has(functionalCapacity.id);
	}

	onSelectModeSelectionChange(capacity: FunctionalCapacity, event: MatCheckboxChange): void {
		if (capacity.children && capacity.children.length > 0) {
			return;
		}

		if (event.checked) {
			this.selectedFunctionalCapacityIds.add(capacity.id);
		} else {
			this.selectedFunctionalCapacityIds.delete(capacity.id);
		}
	}

	createFunctionalCapacity(): void {
		if (this.createFunctionalCapacityFormGroup.valid) {
			const form: FunctionalCapacityCreateForm = {
				groupId: this.createFunctionalCapacityFormGroup.get('group')!.value.id,
				name: this.createFunctionalCapacityFormGroup.get('name')!.value
			}

			this.subscription.add(this.switchLoading()
				.pipe(
					switchMap(() => this.functionalCapacitiesService.createFunctionalCapacity(this.tenantId, form)),
					tap((result) => this.displayUpdatedSucceed(result !== null)),
					tap((result) => {
						if (result) {
							const group = this.tenantFunctionalCapacities.find(group => group.id === form.groupId);
							if (group) {
								group.children.unshift(result);
							}
							this.filteredFunctionalCapacities = this.tenantFunctionalCapacities;
							this.toggleMode('select');
						}
					}),
					switchMap((result) => {
						if (result) {
							this.selectedFunctionalCapacityIds.add(result.id)

							const form: FunctionalCapacityForm = {
								functionalCapacityIds: Array.from(this.selectedFunctionalCapacityIds)
							}

							return this.functionalCapacitiesService.updateApplicationFunctionalCapacityList(this.tenantId, this.application.applicationId, form)
						}

						return of(null);
					}),
					tap(() => this.emitUpdate = true),
					finalize(() => this.switchLoading())
				).subscribe());
		}
	}

	createFunctionalCapacityGroup(): void {
		if (this.createFunctionalCapacityGroupFormGroup.valid) {
			const form: FunctionalCapacityGroupCreateForm = {
				name: this.createFunctionalCapacityGroupFormGroup.get('name')!.value,
				icon: this.createFunctionalCapacityGroupFormGroup.get('icon')!.value
			}

			this.subscription.add(this.switchLoading()
				.pipe(
					switchMap(() => this.functionalCapacitiesService.createFunctionalCapacityGroup(this.tenantId, form)),
					tap((result) => this.displayUpdatedSucceed(result !== null)),
					tap((result) => {
						if (result) {
							this.tenantFunctionalCapacities.unshift(result);
							this.filteredFunctionalCapacities = this.tenantFunctionalCapacities;
							this.toggleMode('select');
						}
					}),
					finalize(() => this.switchLoading())
				).subscribe());
		}
	}

	areAllChildrenSelectedForDelete(parent: FunctionalCapacity): boolean {
		if (!parent.children || parent.children.length === 0) {
			return false;
		}

		return parent.children.every(child => this.functionalCapacitiesToDelete.has(child.id));
	}

	areSomeChildrenSelectedForDelete(parent: FunctionalCapacity): boolean {
		if (!parent.children || parent.children.length === 0) {
			return false;
		}

		const selectedChildren = parent.children.filter(child => this.functionalCapacitiesToDelete.has(child.id));
		return selectedChildren.length > 0 && selectedChildren.length < parent.children.length;
	}

	findParentOfCapacity(capacityId: string): FunctionalCapacity | null {
		for (const parent of this.filteredFunctionalCapacities) {
			if (parent.children && parent.children.some(child => child.id === capacityId)) {
				return parent;
			}
		}
		return null;
	}

	isCapacitySelectedForDelete(capacity: FunctionalCapacity): boolean {
		return this.functionalCapacitiesToDelete.has(capacity.id);
	}

	isParentIndeterminate(parent: FunctionalCapacity): boolean {
		return this.parentsWithSelectedChildren.has(parent.id) && !this.functionalCapacitiesToDelete.has(parent.id);
	}

	isParentCapacity(capacity: FunctionalCapacity): boolean {
		return capacity.children && capacity.children.length > 0;
	}

	isSelectModeCheckboxDisabled(capacity: FunctionalCapacity): boolean {
		return this.mode === 'select' && this.isParentCapacity(capacity);
	}

	displayUpdatedSucceed(updateSucceed: boolean): void {
		if (updateSucceed) {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.success'));
		} else {
			this.snackbarService.show(this.translate.instant('page.application.detail.update.fail'), 'danger-snack');
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}
